import { createContext, JSX, onCleanup, useContext, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { v4 as uuidv4, validate as validateUUID } from "uuid"
import PouchDB from "pouchdb-browser"
import PouchDBFind from "pouchdb-find"
PouchDB.plugin(PouchDBFind)

import itemsStore from "@/stores/itemsStore";
import Types from "@/types"
import { uploadFileMultipart } from "@/utilities/multipartUpload"

const { VITE_DISABLE_SYNC, DEV } = import.meta.env

// window process polyfill
// @ts-ignore
window.process = {
  nextTick: (fn, ...args) => setTimeout(() => fn(...args)),
};

export interface User {
  id: string
  name?: string
  nameId?: string
}

export enum DataTypes {
  Reference = "Reference",
  String = "Text",
  RichText = "Rich Text",
  Number = "Number",
  Boolean = "Boolean",
  Date = "Date",
  Array = "Array",
  Object = "Object",
}

export interface ConnectionData {
  origin: string
  type: Types
  target: any
  targetType: DataTypes
}

export interface Connection extends ConnectionData {
  _id: string
  kind: "relationship" | "property"
  createdAt: string
  createdBy: string
  addedAt: string
  addedBy: string
  updatedAt?: string
  updatedBy?: string
}

export enum MediaVersions {
  Original = Types.Original,
}

export enum MediaTypes {
  Image = "Image",
  Video = "Video",
  Audio = "Audio",
  Document = "Document",
  Other = "Other",
}

export interface MediaData {
  type: MediaTypes
  original?: string
}

export interface Media extends MediaData {
  id: string
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
}

interface LocalStore {
  database: PouchDB.Database<Connection> | null
  createConnection: (connectionData: ConnectionData, id?: string) => Promise<void>
  updateConnection: (id: string, newTarget: any, newTargetType?: DataTypes) => Promise<void>
  upsertConnection: (connectionData: ConnectionData, id?: string) => Promise<void>

  createSpecialConnection: (path: {
    origin?: string,
    type?: Types,
    target?: string,
  }, connectionData?: ConnectionData, id?: string) => Promise<void>

  createMedia: (file: File, id?: string) => Promise<void>
  deleteMedia: (id: string) => Promise<void>

  activeUserId: string | null
  activeUser: User | null
  setActiveUser: (id: string, name?: string) => Promise<void>
  removeActiveUser: () => void
  getStoredCredential: (credentialId: string) => any
}

const [localStore, setLocalStore] = createStore({
  database: null,
  createConnection: async (connectionData, id = uuidv4()) => {
    if (!localStore.database) {
      throw new Error("No database")
    }
    const createdAt = new Date().toISOString()
    const connection = await localStore.database.put({
      _id: id,
      ...connectionData,
      kind: connectionData.targetType === DataTypes.Reference ? "relationship" : "property",
      createdAt,
      createdBy: localStore.activeUserId!,
      addedAt: createdAt,
      addedBy: localStore.activeUserId!
    })
  },
  updateConnection: async (id, newTarget, newTargetType) => {
    if (!localStore.database) {
      throw new Error("No database")
    }
    const connection = await localStore.database.get(id)
    if (!connection) {
      throw new Error("No connection found")
    }

    const updatedAt = new Date().toISOString()

    localStore.database.put({
      ...connection,
      target: newTarget,
      targetType: newTargetType || connection.targetType,
      updatedAt,
      updatedBy: localStore.activeUserId!
    })
  },

  createSpecialConnection: async (path, connectionData, id = uuidv4()) => {
    if (!localStore.database) {
      throw new Error("No database")
    }
    const createdAt = new Date().toISOString()
    const _id = `${path.origin}:${path.type}:${path.target}`
    const existingConnection = await localStore.database.get(_id).catch(() => null)
    // if (existingConnection) {
    //   await localStore.database.put({
    //     ...existingConnection,
    //     updatedAt: createdAt,
    //     updatedBy: localStore.activeUserId!
    //   })
    //   return
    // }
    // await localStore.database.put({
    //   _id,
    //   ...connectionData,
    //   kind: connectionData.targetType === DataTypes.Reference ? "relationship" : "property",
    //   createdAt,
    //   createdBy: localStore.activeUserId!,
    // })
  },

  createMedia: async (file, id = uuidv4()) => {
    if (!localStore.database) {
      throw new Error("No database")
    }

    if (file.name) {
      await localStore.createConnection({
        origin: id,
        type: Types.Name,
        target: file.name,
        targetType: DataTypes.String
      })
    }

    // category media
    await localStore.createConnection({
      origin: id,
      type: Types.Category,
      target: Types.Media,
      targetType: DataTypes.Reference
    })

    // Determine media type
    let type = MediaTypes.Other
    if (file.type.startsWith('image')) {
      type = MediaTypes.Image
    } else if (file.type.startsWith('video')) {
      type = MediaTypes.Video
    } else if (file.type.startsWith('audio')) {
      type = MediaTypes.Audio
    } else if (file.type.startsWith('application/pdf')) {
      type = MediaTypes.Document
    }

    // media type
    await localStore.createConnection({
      origin: id,
      type: Types.MediaType,
      target: type,
      targetType: DataTypes.Reference
    })

    // mime type
    await localStore.createConnection({
      origin: id,
      type: Types.MimeType,
      target: file.type,
      targetType: DataTypes.String
    })

    const serverUrl = "https://media.trettin.org:8443"

    try {
      // Use multipart upload for all files (more efficient even for smaller files)
      const uploadResult = await uploadFileMultipart({
        serverUrl,
        appId: 'stuff',
        userId: localStore.activeUserId!,
        blobId: id,
        version: Types.Original,
        file,
        onProgress: (progress) => {
          console.log(`Upload progress: ${Math.round(progress * 100)}%`);
          // Optionally update UI with progress
        }
      });

      if (!uploadResult.success) {
        throw new Error(`Upload failed: ${uploadResult.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },
  deleteMedia: async (id) => {
    if (!localStore.database) {
      throw new Error("No database")
    }
    const serverUrl = "https://media.trettin.org:8443"
    const appId = "stuff"
    const userId = localStore.activeUserId!
    const blobId = id
    const res = await fetch(`${serverUrl}/api/blobs/delete/${appId}/${userId}/${blobId}`, {
      method: "DELETE"
    })
    if (!res.ok) {
      throw new Error(`Failed to delete media: ${res.statusText}`)
    }
    // delete all connections
    const connections = await localStore.database.find({
      selector: {
        origin: id
      }
    })
    await Promise.all(connections.docs.map((doc: any) => localStore.database!.remove(doc)))
  },

  activeUserId: null,
  activeUser: null,
  setActiveUser: async (id, name) => {
    if (!id || !validateUUID(id)) {
      console.error("Invalid user ID:", id)
      return
    }
    setLocalStore("activeUserId", id)
    setLocalStore("activeUser", { id })
    localStorage.setItem("activeUserId", id)

    const databaseName = `stuff_${id}_connections`
    const pouch = new PouchDB(databaseName) as PouchDB.Database<Connection>

    setLocalStore("database", pouch)

    // if name is provided, create a connection
    if (name) {
      await localStore.createConnection({
        origin: id,
        type: Types.Name,
        target: name,
        targetType: DataTypes.String
      })
    }

    // TODO: subscribe to name changes and update activeUser name
    // pouch.changes({
    //   since: 'now',
    //   live: true,
    //   include_docs: true
    // }).on('change', (change) => {
    //   if (change.doc.type === Types.Name) {
    //     setLocalStore("activeUser", { id, name: change.doc.target, nameId: change.doc._id })
    //   }
    // })

    // rx.collections.connections.find().$.subscribe((connections) => {
    //   const nameConnection = connections.find((c) => c.type === "name")
    //   if (nameConnection) {
    //     setLocalStore("activeUser", { id, name: nameConnection.target, nameId: nameConnection.id })
    //   }
    // })

    if (DEV && VITE_DISABLE_SYNC === "true") return

    let replicateConnectionsCouchDB: PouchDB.Replication.Sync<Connection> | null = null

    try {
      const couchUser = "admin"
      const couchPassword = "admin"
      const remoteURL = `https://${couchUser}:${couchPassword}@couchdb.trettin.org:8443/${databaseName}/`

      replicateConnectionsCouchDB = pouch.sync(remoteURL, {
        live: true,
      }) as PouchDB.Replication.Sync<Connection>
      replicateConnectionsCouchDB
        .on('change', function (info) {
          // handle change
        }).on('paused', function (err) {
          // replication paused (e.g. replication up to date, user went offline)
        }).on('active', function () {
          // replicate resumed (e.g. new changes replicating, user went back online)
        }).on('denied', function (err) {
          // a document failed to replicate (e.g. due to permissions)
        }).on('complete', function (info) {
          // handle complete
        }).on('error', function (err) {
          // handle error
        })
    } catch (error) {
      console.error("Failed to replicate:", error)
    }

    // cleanup
    return () => {
      if (replicateConnectionsCouchDB) replicateConnectionsCouchDB.cancel()
    }
  },
  removeActiveUser: () => {
    setLocalStore("activeUserId", null)
    setLocalStore("activeUser", null)
    localStorage.removeItem("activeUserId")
  },
  getStoredCredential: (credentialId) => {
    const credentials = JSON.parse(localStorage.getItem("webauthn-credentials") || "{}")
    return credentials[credentialId]
  },
} as LocalStore)

const state = {
  local: localStore,
  items: itemsStore
}

export const StateContext = createContext(state)

export const StateProvider = ({ children }: { children: JSX.Element }) => {
  // refresh user on change
  const handleStorage = (event: StorageEvent) => {
    if (event.key === "activeUserId" && event.newValue) {
      localStore.setActiveUser(event.newValue)  // Pass just the ID
    }
  }

  // Initial load
  onMount(() => {
    const storedId = localStorage.getItem("activeUserId")
    if (storedId) {
      localStore.setActiveUser(storedId)
    }
  })

  window.addEventListener("storage", handleStorage)

  onCleanup(() => {
    // storage event listener cleanup
    window.removeEventListener("storage", handleStorage)
  })

  return <StateContext.Provider value={state}>{children}</StateContext.Provider>
}

export function useLocalStore() {
  return useContext(StateContext).local
}

export function useItemsStore() {
  return useContext(StateContext).items
}
